import React, { useEffect, useRef } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import { CacheProvider } from '@emotion/react';
import { Box } from '@mui/material';
import { Route, Switch, Redirect, useRouteMatch } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';

import { appTheme, colors, spacing } from '../../common/theme/index';
import { cacheRtl } from '../../theme';
import {
  PersonalZoneNavbar,
  PersonalInfoSection,
  CouponsTableSection,
  SavingsSection,
  FavoritesSection,
  OrderHistorySection,
  MembershipCertificateSection,
} from '.';
import { useTranslations } from 'common/hooks/useTranslations';

import { USER_INFO_MENU_IDS, USER_INFO_MENU_CONFIG } from './utils/enums';
import * as routes from '../../constants/routes';
import { addPopup } from '../../redux/actions';
import * as popups from '../../constants/popup-types';

const PersonalZoneContent = () => {
  const { path } = useRouteMatch();
  const dispatch = useDispatch();
  const userData = useSelector((store) => store.config.UserData);
  const { translate } = useTranslations();
  const hasShownLoginPopupRef = useRef(false);

  useEffect(() => {
    if (userData) {
      hasShownLoginPopupRef.current = false;
      return;
    }
    if (hasShownLoginPopupRef.current) return;
    hasShownLoginPopupRef.current = true;
    dispatch(
      addPopup({
        type: popups.LOGIN,
        subtitleText: translate('personal_zone_login_title'),
        redirectPathWhenNotLoggedIn: '/',
      })
    );
  }, [userData, dispatch, translate]);
  

  const renderSection = (id) => {
    if (!userData) return null;

    switch (id) {
      case USER_INFO_MENU_IDS.PERSONAL_INFO:
        return <PersonalInfoSection testID={'personal-info-section'} />;
      case USER_INFO_MENU_IDS.COUPONS:
        return <CouponsTableSection testID={'coupons-section'} />;
      case USER_INFO_MENU_IDS.SAVINGS:
        return <SavingsSection testID={'savings-section'} />;
      case USER_INFO_MENU_IDS.FAVORITES:
        return <FavoritesSection testID={'favorites-section'} />;
      case USER_INFO_MENU_IDS.ORDER_HISTORY:
        return <OrderHistorySection testID={'order-history-section'} />;
      case USER_INFO_MENU_IDS.MEMBERSHIP_CERTIFICATE:
        return <MembershipCertificateSection testID={'membership-certificate-section'} />;
      default:
        return <PersonalInfoSection testID={'personal-info-section'} />;
    }
  };

  if (!userData) {
    return <Box sx={{ minHeight: '100vh', backgroundColor: colors.palette.white }} />;
  }

  return (
    <Box
      testId="user-info-page"
      dir="rtl"
      sx={{
        minHeight: '100vh',
        backgroundColor: colors.palette.white,
        display: 'flex',
        justifyContent: 'center',
        pt: { xs: 0, md: 2, lg: 4 },
        pb: { xs: 2, md: 2, lg: 4 },
        px: { xs: 0, md: 2, lg: 4 },
        overflow: 'visible !important',
      }}
    >
      <Box
        testId="user-info-container"
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: { xs: 0, md: 2, lg: 3 },
          width: '100%',
          maxWidth: 1400,
        }}
      >
        <PersonalZoneNavbar testID={'user-info-menu'} />

        <Box
          testId={'main-content'}
          sx={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: { xs: 0, md: `${spacing.sm}px` },
            p: { xs: 2, md: 0 },
            backgroundColor: colors.palette.white,
          }}
        >
          <Switch>
            <Route exact path={path}>
              <Redirect to={`${path}${routes.user_info_routes.personal_info}`} />
            </Route>

            {USER_INFO_MENU_CONFIG.filter(item => item.path).map((item) => (
              <Route 
                key={item.id} 
                path={`${path}${item.path}`}
              >
                {renderSection(item.id)}
              </Route>
            ))}
          </Switch>
        </Box>
      </Box>
    </Box>
  );
};

const PersonalZoneDemo = () => (
  <CacheProvider value={cacheRtl}>
    <ThemeProvider theme={appTheme}>
      <PersonalZoneContent />
    </ThemeProvider>
  </CacheProvider>
);

export default PersonalZoneDemo;